import React, { CSSProperties, useCallback, useContext, useMemo, useState } from 'react';
import { FaOptionIcon, FaOptionIconProps, NamedIconsType } from '@europrocurement/l2d-icons';
import {
    Box,
    Collapse,
    lighten,
    PaletteColor,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import './MenuItem.css';
import { MenuContext } from '@europrocurement/flexy-components/providers';
import { IconName } from '@fortawesome/pro-duotone-svg-icons';
import { Link } from 'react-router-dom';
// import { Link } from 'react-router-dom';

export type MenuItemProps = {
    identifier: string;
    label: string;
    icon?: FaOptionIconProps;
    onClick?: (target: string | null) => void;
    onSidebarClose?: () => void;
    href?: string;
    color?: PaletteColor;
    childrenItems?: MenuItemProps[];
    folded?: boolean;
    ancestors?: string[];
    header?: boolean;
    hidden?: boolean;
    active?: boolean;
};

const isItemActive = (activeTree: string[], identifier: string): boolean =>
    activeTree.indexOf(identifier) !== -1 &&
    activeTree.length - 1 === activeTree.indexOf(identifier);

export const MenuItem: React.FunctionComponent<MenuItemProps> = function (props) {
    const {
        identifier,
        label,
        icon,
        onClick = () => {},
        onSidebarClose = undefined,
        href = null,
        color,
        childrenItems = [],
        folded = false,
        ancestors = [],
        header = false,
        hidden = false,
    } = props;
    const theme = useTheme();
    const { activeTree, setActiveTree, openElements, setOpenElements } = useContext(MenuContext);
    const [hover, setHover] = useState<boolean>(false);
    const [collapsed, setCollapsed] = useState<boolean>(openElements.indexOf(identifier) === -1);
    const activeElement = isItemActive(activeTree, identifier);
    const { palette, breakpoints } = theme;
    const downSmBreakpoint = useMediaQuery(breakpoints.down('sm'));
    const mainColor = color || palette.primary;
    const boxClassNames = ['menu-item'];
    if (folded) {
        boxClassNames.push('menu-item-folded');
    }
    const childrenAncestors = [...ancestors, identifier];
    const childrenNodes = childrenItems.map((child) => (
        <MenuItem
            key={child.identifier}
            {...child}
            folded={folded}
            ancestors={childrenAncestors}
        />
    ));

    const handleClickChevron = () => {
        const idx = openElements.indexOf(identifier);
        if (idx !== -1) {
            openElements.splice(idx, 1);
        } else {
            openElements.push(identifier);
        }

        setOpenElements(openElements);
        setCollapsed(openElements.indexOf(identifier) === -1);
    };

    const handleClick = () => {
        if (childrenItems.length > 0) {
            handleClickChevron();
        }

        if (href) {
            setActiveTree(childrenAncestors);
        }

        if (onClick) {
            onClick(href || null);
        }

        if (downSmBreakpoint && onSidebarClose) {
            onSidebarClose();
        }
    };

    const containerStyle: CSSProperties = {
        borderRightColor: activeElement || hover ? mainColor.main : 'transparent',
        fontWeight: activeElement || hover ? 600 : 500,
        paddingLeft: folded ? `4px` : `${(20 + (childrenAncestors.length - 1) * 15).toString()}px`,
        paddingRight: folded ? '0' : '10px',
    };

    if (activeTree.indexOf(identifier) !== -1 && !activeElement) {
        if (palette.mode === 'light') {
            containerStyle.borderRightColor = lighten(mainColor.main, 0.8);
            containerStyle.fontWeight = 500;
        } else {
            containerStyle.borderRightColor = lighten(mainColor.main, 0.2);
        }
    }

    let chevron = null;
    if (childrenItems.length > 0 && !folded) {
        chevron = collapsed ? (
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClickChevron();
                }}
                sx={{ marginLeft: 'auto' }}
            >
                <FaOptionIcon
                    icon={['fasl', 'chevron-right']}
                    sx={{ display: 'flex', alignItems: 'center' }}
                />
            </Box>
        ) : (
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClickChevron();
                }}
                sx={{ marginLeft: 'auto' }}
            >
                <FaOptionIcon
                    icon={['fasl', 'chevron-down']}
                    sx={{ display: 'flex', alignItems: 'center' }}
                />
            </Box>
        );
    }

    type IconPropsType = {
        // Define known properties or use `Record<string, unknown>` if uncertain
        size: string;
        color?: string;
        [key: string]: unknown;
    };

    // Minimal type for icon
    type MinimalIcon = {
        iconName: string;
        iconProps: IconPropsType;
    };

    // Type guard to check if `icon` is of type `MinimalIcon`
    const isMinimalIcon = useCallback(
        (minimalIcon: unknown): minimalIcon is MinimalIcon =>
            typeof minimalIcon === 'object' &&
            minimalIcon !== null &&
            'iconName' in minimalIcon &&
            typeof (minimalIcon as MinimalIcon).iconName === 'string',
        [],
    );

    /**
     * Retrieve icon props from string name or NamedIconsType object.
     *
     * NamedIconsType is used to centralize the props of icons.
     */
    const iconProps = useMemo(() => {
        let foundProps: FaOptionIconProps | null = null;

        if (icon && Array.isArray(icon.icon)) {
            if (typeof icon.icon[1] === 'string') {
                foundProps = icon;
            } else if (typeof icon.icon[1] === 'object') {
                const namedIconProps = (icon.icon[1] as NamedIconsType).props;

                if (isMinimalIcon(namedIconProps.icon)) {
                    foundProps = {
                        icon: ['fasl', namedIconProps.icon.iconName as IconName],
                        iconProps: namedIconProps.iconProps,
                    };

                    if (isMinimalIcon(namedIconProps.option)) {
                        foundProps = {
                            ...foundProps,
                            option: ['fas', namedIconProps.option.iconName as IconName],
                            optionProps: namedIconProps.option.iconProps,
                        };
                    }
                }
            }
        }

        return foundProps;
    }, [icon, isMinimalIcon]);

    if (hidden) return null;

    if (header) {
        return (
            <Box
                className={boxClassNames.join(' ')}
                data-testid="menu-item"
            >
                <Tooltip
                    title={folded ? [...ancestors, label].join(' -> ') : ''}
                    placement="right"
                >
                    <Typography
                        style={containerStyle}
                        variant="l2dcaption"
                        color="text.gris50"
                    >
                        {folded ? '' : label}
                    </Typography>
                </Tooltip>
            </Box>
        );
    }

    return (
        <Box
            className={boxClassNames.join(' ')}
            data-testid="menu-item"
        >
            <Tooltip
                title={folded ? [...ancestors, label].join(' -> ') : ''}
                placement="right"
            >
                {href ? (
                    <Link
                        to={href}
                        className="menu-item-link"
                        data-testid="menu-item-link"
                        style={containerStyle}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                    >
                        {iconProps ? <FaOptionIcon {...iconProps} /> : null}
                        {folded ? '' : label}
                        {chevron}
                    </Link>
                ) : (
                    <Box
                        onClick={handleClick}
                        className="menu-item-link"
                        data-testid="menu-item-link"
                        style={containerStyle}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                    >
                        {iconProps ? <FaOptionIcon {...iconProps} /> : null}
                        {folded ? '' : label}
                        {chevron}
                    </Box>
                )}
            </Tooltip>
            <Collapse
                in={!collapsed}
                timeout="auto"
                unmountOnExit
            >
                {childrenNodes}
            </Collapse>
        </Box>
    );
};
